import React, { useState, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  Image,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
} from "react-native";
import { router, useFocusEffect } from "expo-router";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import i18n from "../../locales/i18n";
import { useStock } from "../../hooks/useStock";
import WebSidebar from "./WebSidebar";

// Import the stock image for header
import StockHeaderImage from "../../assets/images/stock.png";
import SearchIcon from "../../assets/web/scanner/search.png";

interface Product {
  id_product: number;
  name: string;
  price_sell: string;
  price_bought: string;
  stock: number;
  photo?: string;
  type_quantity?: string;
  category?: string;
  created_at?: string;
  barcode?: string | number;
}

const WebStockScreen: React.FC = () => {
  const { products, isLoading, fetchProducts, deleteProduct } = useStock();
  const { locale } = useSelector((state: RootState) => state.app);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [selectedSort, setSelectedSort] = useState("name_asc");
  const [deleteConfirm, setDeleteConfirm] = useState<{
    visible: boolean;
    product: Product | null;
  }>({
    visible: false,
    product: null,
  });
  const [isDeleting, setIsDeleting] = useState(false);

  // Check if current language is RTL (Arabic)
  const isRTL = locale === "ar" || i18n.locale === "ar";

  // Sort options
  const sortOptions = [
    { value: "name_asc", label: i18n.t("name_asc") },
    { value: "name_desc", label: i18n.t("name_desc") },
    { value: "price_asc", label: i18n.t("price_asc") },
    {
      value: "price_desc",
      label: i18n.t("price_desc"),
    },
    { value: "stock_asc", label: i18n.t("stock_asc") },
    {
      value: "stock_desc",
      label: i18n.t("stock_desc"),
    },
  ];

  // Fetch products on mount
  useFocusEffect(
    useCallback(() => {
      fetchProducts(true);
    }, [fetchProducts]),
  );

  // Sort function
  const sortProducts = (productsToSort: Product[]) => {
    return [...productsToSort].sort((a, b) => {
      switch (selectedSort) {
        case "name_asc":
          return a.name.localeCompare(b.name);
        case "name_desc":
          return b.name.localeCompare(a.name);
        case "price_asc":
          return parseFloat(a.price_sell) - parseFloat(b.price_sell);
        case "price_desc":
          return parseFloat(b.price_sell) - parseFloat(a.price_sell);
        case "stock_asc":
          return a.stock - b.stock;
        case "stock_desc":
          return b.stock - a.stock;
        default:
          return 0;
      }
    });
  };

  // Filter and sort products based on search and sort
  const filteredProducts = sortProducts(
    searchQuery.trim()
      ? products.filter((p: Product) =>
          p.name.toLowerCase().includes(searchQuery.toLowerCase()),
        )
      : products,
  );

  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "-";
    return `${date.getDate().toString().padStart(2, "0")}/${(date.getMonth() + 1).toString().padStart(2, "0")}/${date.getFullYear()}`;
  };

  const handleProductPress = (product: Product) => {
    // Navigate to add product page in edit mode with product data
    router.push({
      pathname: "/(drawer)/addproduct",
      params: {
        mode: "edit",
        productId: product.id_product.toString(),
        productName: product.name,
        priceSell: product.price_sell,
        priceBought: product.price_bought,
        stock: product.stock.toString(),
        typeOfQuantity: product.type_quantity || "unit",
        barcode: product.barcode || "",
        photo: product.photo || "",
      },
    });
  };

  const handleAddProduct = () => {
    router.push("/(drawer)/addproduct");
  };

  const handleDeletePress = (e: any, product: Product) => {
    e.stopPropagation();
    setDeleteConfirm({ visible: true, product });
  };

  const confirmDeleteProduct = async () => {
    if (!deleteConfirm.product) return;
    setIsDeleting(true);
    await deleteProduct(deleteConfirm.product.id_product);
    setIsDeleting(false);
    setDeleteConfirm({ visible: false, product: null });
  };

  return (
    <div
      key={`stock-screen-${locale}`}
      className="flex flex-1 bg-[#F5F5F5] min-h-screen flex-row"
    >
      <WebSidebar />

      <View style={styles.mainContent}>
        <ScrollView
          style={styles.scrollView}
          showsVerticalScrollIndicator={false}
        >
          {/* Header with blue bar */}
          <View style={styles.headerSection}>
            <div
              className={`flex flex-row items-center gap-4 ${isRTL ? "justify-end flex-row-reverse" : "justify-start"}`}
            >
              <View style={styles.headerIconContainer}>
                <Image
                  source={StockHeaderImage}
                  style={styles.headerIcon}
                  resizeMode="contain"
                />
              </View>
              <Text style={styles.headerTitle}>{i18n.t("stock")}</Text>
            </div>
            <View style={styles.headerBar} />
          </View>

          {/* Controls Row */}
          <View style={styles.controlsRow}>
            {/* Add Product Button */}

            {/* Search Bar */}
            <View style={styles.searchContainer}>
              <Image
                source={SearchIcon}
                style={styles.searchIcon}
                resizeMode="contain"
              />
              <TextInput
                style={styles.searchInput}
                placeholder={i18n.t("search")}
                value={searchQuery}
                onChangeText={setSearchQuery}
                placeholderTextColor="#999"
              />
            </View>
            {/* Sort Dropdown */}
            <View style={styles.sortContainer}>
              <TouchableOpacity
                style={styles.sortButton}
                onPress={() => setShowSortMenu(!showSortMenu)}
              >
                <Text style={styles.sortButtonText}>
                  {sortOptions.find((o) => o.value === selectedSort)?.label ||
                    i18n.t("sort_by")}
                </Text>
                <Text style={styles.sortIcon}>{showSortMenu ? "▲" : "▼"}</Text>
              </TouchableOpacity>

              {showSortMenu && (
                <View style={styles.sortDropdown}>
                  {sortOptions.map((option) => (
                    <TouchableOpacity
                      key={option.value}
                      style={[
                        styles.sortOption,
                        selectedSort === option.value &&
                          styles.sortOptionActive,
                      ]}
                      onPress={() => {
                        setSelectedSort(option.value);
                        setShowSortMenu(false);
                      }}
                    >
                      <Text
                        style={[
                          styles.sortOptionText,
                          selectedSort === option.value &&
                            styles.sortOptionTextActive,
                        ]}
                      >
                        {option.label}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              )}
            </View>

            <TouchableOpacity
              style={styles.addButton}
              onPress={handleAddProduct}
            >
              <Text style={styles.addButtonText}>{i18n.t("addproduct")}</Text>
              <Text style={styles.addButtonIcon}>+</Text>
            </TouchableOpacity>
          </View>

          {/* Products Table */}
          <View style={styles.tableContainer}>
            {/* Table Header */}
            <View
              style={[
                styles.tableHeader,
                isRTL && { flexDirection: "row-reverse" },
              ]}
            >
              <Text style={[styles.headerCell, styles.actionColumn]}>{""}</Text>
              <Text style={[styles.headerCell, styles.priceColumn]}>
                {i18n.t("boughtprice")}
              </Text>
              <Text style={[styles.headerCell, styles.priceColumn]}>
                {i18n.t("sellprice")}
              </Text>
              <Text style={[styles.headerCell, styles.stockColumn]}>
                {i18n.t("quantity")}
              </Text>
              <Text style={[styles.headerCell, styles.categoryColumn]}>
                {i18n.t("storeCategory")}
              </Text>
              <Text style={[styles.headerCell, styles.nameColumn]}>
                {i18n.t("productname")}
              </Text>
              <Text style={[styles.headerCell, styles.imageColumn]}></Text>
            </View>

            {/* Loading State */}
            {isLoading ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color="#1379A2" />
              </View>
            ) : filteredProducts.length === 0 ? (
              <View style={styles.emptyContainer}>
                <Text style={styles.emptyText}>{i18n.t("noproducts")}</Text>
              </View>
            ) : (
              /* Table Rows */
              filteredProducts.map((product: Product, index: number) => (
                <TouchableOpacity
                  key={product.id_product}
                  style={[
                    styles.tableRow,
                    index % 2 === 0 ? styles.rowEven : styles.rowOdd,
                    isRTL && { flexDirection: "row-reverse" },
                  ]}
                  onPress={() => handleProductPress(product)}
                >
                  <View style={styles.actionColumn}>
                    <TouchableOpacity
                      style={styles.deleteButton}
                      onPress={(e) => handleDeletePress(e, product)}
                    >
                      <Text style={styles.deleteButtonText}>🗑️</Text>
                    </TouchableOpacity>
                  </View>
                  <Text style={[styles.cell, styles.priceColumn]}>
                    {parseFloat(product.price_bought).toLocaleString()}{" "}
                    {i18n.t("currency")}
                  </Text>
                  <Text style={[styles.cell, styles.priceColumn]}>
                    {parseFloat(product.price_sell).toLocaleString()}{" "}
                    {i18n.t("currency")}
                  </Text>
                  <Text style={[styles.cell, styles.stockColumn]}>
                    {product.stock || "-"}
                  </Text>
                  <Text style={[styles.cell, styles.categoryColumn]}>
                    {product.category || i18n.t("general")}
                  </Text>
                  <Text
                    style={[styles.cell, styles.nameColumn]}
                    numberOfLines={1}
                  >
                    {product.name}
                  </Text>
                  <View style={styles.imageColumn}>
                    {product.photo ? (
                      <Image
                        source={{ uri: product.photo }}
                        style={styles.productImage}
                        resizeMode="cover"
                      />
                    ) : (
                      <View style={styles.imagePlaceholder}>
                        <Text style={styles.placeholderText}>📦</Text>
                      </View>
                    )}
                  </View>
                </TouchableOpacity>
              ))
            )}
          </View>
        </ScrollView>
      </View>

      {/* Delete Confirmation Modal */}
      {deleteConfirm.visible && (
        <TouchableOpacity
          activeOpacity={1}
          onPress={() => setDeleteConfirm({ visible: false, product: null })}
          style={styles.modalOverlay}
        >
          <TouchableOpacity
            activeOpacity={1}
            onPress={(e) => e.stopPropagation()}
            style={styles.modalContent}
          >
            <Text style={styles.modalTitle}>{i18n.t("deleteProduct")}</Text>
            <Text style={styles.modalMessage}>{i18n.t("confirmDelete")}</Text>
            <Text style={styles.modalProductName}>
              {deleteConfirm.product?.name}
            </Text>
            <View style={styles.modalActions}>
              <TouchableOpacity
                style={styles.modalCancelButton}
                onPress={() =>
                  setDeleteConfirm({ visible: false, product: null })
                }
                disabled={isDeleting}
              >
                <Text style={styles.modalCancelText}>{i18n.t("cancel")}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.modalDeleteButton,
                  isDeleting && { opacity: 0.6 },
                ]}
                onPress={confirmDeleteProduct}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <ActivityIndicator size="small" color="#FFF" />
                ) : (
                  <Text style={styles.modalDeleteText}>{i18n.t("delete")}</Text>
                )}
              </TouchableOpacity>
            </View>
          </TouchableOpacity>
        </TouchableOpacity>
      )}
    </div>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F5F5F5",
    minHeight: "100vh" as any,
  },
  containerRTL: {
    flexDirection: "row-reverse",
  } as any,
  mainContent: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
    padding: 20,
  },
  headerSection: {
    marginBottom: 20,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 16,
    paddingVertical: 16,
    paddingHorizontal: 24,
  },
  headerIconContainer: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: "#E8F4F8",
    borderWidth: 4,
    borderColor: "#1379A2",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  headerTitle: {
    fontSize: 32,
    fontWeight: "bold",
    color: "#333",
  },
  headerIcon: {
    width: 70,
    height: 70,
  },
  headerBar: {
    height: 8,
    backgroundColor: "#1379A2",
    borderRadius: 4,
    marginTop: 8,
  },
  controlsRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    marginBottom: 20,
    zIndex: 100,
    position: "relative" as any,
  },
  addButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#333",
    borderRadius: 25,
    paddingVertical: 10,
    paddingHorizontal: 20,
    gap: 8,
  },
  addButtonIcon: {
    color: "#FFFFFF",
    fontSize: 20,
    fontWeight: "bold",
  },
  addButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "500",
  },
  filterButton: {
    width: 44,
    height: 44,
    borderRadius: 8,
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#E0E0E0",
    alignItems: "center",
    justifyContent: "center",
  },
  filterIcon: {
    fontSize: 22,
  },
  sortContainer: {
    position: "relative" as any,
    zIndex: 1000,
  },
  sortButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E0E0E0",
    paddingVertical: 10,
    paddingHorizontal: 16,
    gap: 8,
  },
  sortButtonText: {
    fontSize: 14,
    color: "#333",
    fontWeight: "500",
  },
  sortIcon: {
    fontSize: 12,
    color: "#666",
  },
  sortDropdown: {
    position: "absolute" as any,
    top: "100%" as any,
    right: 0,
    marginTop: 4,
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E0E0E0",
    minWidth: 200,
    zIndex: 9999,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 12,
    elevation: 20,
  },
  sortOption: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#F0F0F0",
  },
  sortOptionActive: {
    backgroundColor: "#E8F4F8",
  },
  sortOptionText: {
    fontSize: 14,
    color: "#333",
    textAlign: "right",
  },
  sortOptionTextActive: {
    color: "#1379A2",
    fontWeight: "600",
  },
  searchContainer: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E0E0E0",
    paddingHorizontal: 12,
  },
  searchInput: {
    flex: 1,
    paddingVertical: 12,
    fontSize: 18,
    textAlign: "right",
  },
  searchIcon: {
    width: 20,
    height: 20,
    marginRight: 8,
  },
  tableContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    overflow: "hidden",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
    zIndex: 1,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#FFFFFF",
    paddingVertical: 16,
    paddingHorizontal: 12,
    borderBottomWidth: 2,
    borderBottomColor: "#E0E0E0",
  },
  headerCell: {
    fontSize: 16,
    fontWeight: "600",
    color: "#1379A2",
    textAlign: "center",
  },
  tableRow: {
    flexDirection: "row",
    paddingVertical: 12,
    paddingHorizontal: 12,
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#F0F0F0",
  },
  rowEven: {
    backgroundColor: "#FFFFFF",
  },
  rowOdd: {
    backgroundColor: "#F5F5F5",
  },
  cell: {
    fontSize: 16,
    color: "#333",
    textAlign: "center",
  },
  imageColumn: {
    width: 60,
    alignItems: "center",
  },
  nameColumn: {
    flex: 2,
    textAlign: "right",
    paddingHorizontal: 8,
    color: "#1379A2",
    fontWeight: "500",
  },
  categoryColumn: {
    flex: 1,
  },
  stockColumn: {
    flex: 1,
  },
  priceColumn: {
    flex: 1.2,
  },
  dateColumn: {
    flex: 1.2,
  },
  productImage: {
    width: 50,
    height: 50,
    borderRadius: 8,
  },
  imagePlaceholder: {
    width: 50,
    height: 50,
    borderRadius: 8,
    backgroundColor: "#F0F0F0",
    alignItems: "center",
    justifyContent: "center",
  },
  placeholderText: {
    fontSize: 22,
  },
  loadingContainer: {
    padding: 60,
    alignItems: "center",
  },
  emptyContainer: {
    padding: 60,
    alignItems: "center",
  },
  emptyText: {
    color: "#999",
    fontSize: 18,
  },
  actionColumn: {
    width: 50,
    alignItems: "center",
    justifyContent: "center",
  },
  deleteButton: {
    width: 36,
    height: 36,
    borderRadius: 8,
    backgroundColor: "#FEE2E2",
    alignItems: "center",
    justifyContent: "center",
  },
  deleteButtonText: {
    fontSize: 16,
  },
  modalOverlay: {
    position: "absolute" as any,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
  },
  modalContent: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    padding: 32,
    width: 400,
    maxWidth: "90%" as any,
    alignItems: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.25,
    shadowRadius: 24,
    elevation: 20,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: "700" as any,
    color: "#DC2626",
    marginBottom: 12,
  },
  modalMessage: {
    fontSize: 16,
    color: "#666",
    textAlign: "center" as any,
    marginBottom: 8,
  },
  modalProductName: {
    fontSize: 18,
    fontWeight: "600" as any,
    color: "#333",
    marginBottom: 24,
  },
  modalActions: {
    flexDirection: "row" as any,
    gap: 12,
  },
  modalCancelButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 10,
    backgroundColor: "#F3F4F6",
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  modalCancelText: {
    fontSize: 16,
    fontWeight: "500" as any,
    color: "#374151",
  },
  modalDeleteButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 10,
    backgroundColor: "#DC2626",
  },
  modalDeleteText: {
    fontSize: 16,
    fontWeight: "600" as any,
    color: "#FFFFFF",
  },
});

export default WebStockScreen;
